class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState, { open = true } = {}) {
    const version = this.renderVersion = (this.renderVersion || 0) + 1;
    try {
      let sections = parsedState?.sections;
      // An optional header section returning null must not discard a valid
      // drawer. Only fetch again when the required drawer HTML is missing.
      if (!this.getDrawerSource(sections)) sections = await this.fetchSections();
      if (version !== this.renderVersion) return true;
      this.applySections(sections);
      this.productId = parsedState?.id;
      const overlay = this.querySelector('#CartDrawer-Overlay');
      overlay?.addEventListener('click', this.close.bind(this));
      this.refreshVatDisplay();
      if (open) this.open();
      return true;
    } catch (error) {
      console.error('BPC basket content could not refresh.', error);
      return false;
    }
  }

  refresh({ open = false } = {}) {
    return this.renderContents(null, { open });
  }

  async fetchSections() {
    const root = window.Shopify?.routes?.root || '/';
    const cartUrl = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : `${root}cart`;
    const separator = cartUrl.includes('?') ? '&' : '?';
    const response = await fetch(`${cartUrl}${separator}sections=cart-drawer,cart-icon-bubble`, {
      headers: { Accept: 'application/json' }, cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Basket section refresh failed (${response.status}).`);
    return response.json();
  }

  getDrawerSource(sections) {
    if (typeof sections?.['cart-drawer'] !== 'string') return null;
    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const root = html.querySelector('#CartDrawer');
    // Do not erase the current basket if Shopify returns an error page or
    // incomplete section. Validate required nodes before mutating the DOM.
    if (!root?.querySelector('.drawer__inner') || !root.querySelector('cart-drawer-items')) return null;
    return root;
  }

  applySections(sections) {
    const source = this.getDrawerSource(sections);
    const target = this.querySelector('#CartDrawer');
    if (!source || !target) throw new Error('Basket drawer markup was not returned.');
    const empty = source.querySelector('cart-drawer-items').classList.contains('is-empty');
    target.innerHTML = source.innerHTML;
    // Empty-basket CSS is on the OUTER custom element; replacing innerHTML
    // alone leaves a populated basket looking empty.
    this.classList.toggle('is-empty', empty);
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && typeof sections['cart-icon-bubble'] === 'string') {
      const sourceBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (sourceBubble) bubble.innerHTML = sourceBubble.innerHTML;
    }
  }

  refreshVatDisplay() {
    const switcher = document.querySelector('#vat-switcher');
    if (!switcher) return;
    if (window.jQuery) window.jQuery(switcher).trigger('change');
    else switcher.dispatchEvent(new Event('change', { bubbles: true }));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('click', function (event) {
  const clearBtn = event.target.closest('#clear-cart');

  if (!clearBtn) return;

  if (!confirm('Are you sure you want to clear your basket?')) return;

  fetch('/cart/clear.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  .then(() => {
    // reload cart drawer properly in Dawn
    window.location.reload();
  })
  .catch((error) => {
    console.error('Cart clear failed:', error);
  });
});
